import { gql } from 'graphql-request';
import { getGraphQLQueryClient } from './query-client';
import {
  Nullable,
  Store,
  StoreTournament,
  UserRole,
  UserWithToken,
  WithData,
  WithMeta,
} from '@ygoargentina/sdk/api/api.types';
import { z } from 'zod';
import axiosClient from './request-client';
import { AxiosRequestConfig } from 'axios';

export const storeTournamentSchema = z.object({
  storeId: z.number().min(1, 'Selecciona una tienda'),
  title: z.string().nullable().optional(),
  day: z.string().min(1, 'Selecciona un día de la semana'),
  frequency: z.number().min(1, 'La frecuencia debe ser al menos 1 semana'),
  startTime: z.string().min(1, 'Ingresa la hora de inicio'),
  endTime: z.string().min(1, 'Ingresa la hora de fin'),
  formatId: z.number().min(1, 'Selecciona un formato'),
  price: z.number().nullable().optional(),
});

export const getPublicStores = async (variables?: Record<string, unknown>) =>
  getGraphQLQueryClient(null).request<WithMeta<Store>>(
    gql`
      query publicStores($filters: StoreFilters) {
        data: publicStores(filters: $filters) {
          data {
            id
            name
            description
            address
            city
            isOTS
            isCommunity
            zipCode
            latitude
            longitude
            logoUrl
            tags
            openingHours {
              day
              isClosed
              schedule {
                open
                close
              }
            }
            links {
              type
              url
              label
            }
            organizers {
              id
            }
            province {
              name
            }
            countryId
            country {
              id
              name
            }
          }
        }
      }
    `,
    variables
  );

export const getSuggestedStoreTags = async () =>
  getGraphQLQueryClient(null).request<{ data: string[] }>(
    gql`
      query suggestedStoreTags {
        data: suggestedStoreTags
      }
    `
  );

export const getPublicCommunities = async (
  variables?: Record<string, unknown>
) =>
  getGraphQLQueryClient(null).request<WithMeta<Store>>(
    gql`
      query publicCommunities($filters: StoreFilters) {
        data: publicCommunities(filters: $filters) {
          data {
            id
            name
            description
            isCommunity
            logoUrl
            tags
            organizers {
              id
            }
            links {
              type
              url
              label
            }
            recurringTournaments {
              id
              title
              day
              startTime
              endTime
              frequency
              discordChannelId
              format {
                id
                name
              }
              price
            }
            province {
              name
            }
            countryId
            country {
              id
              name
            }
            latitude
            longitude
          }
        }
      }
    `,
    variables
  );

export const listStores = async (
  user: Nullable<UserWithToken>,
  variables?: Record<string, unknown>
) =>
  getGraphQLQueryClient(user).request<WithMeta<Store>>(
    gql`
      query stores($filters: StoreFilters) {
        data: stores(filters: $filters) {
          data {
            id
            name
            description
            address
            image
            city
            isOTS
            isCommunity
            logoUrl
            hidden
            zipCode
            googleMapsUrl
            tags
            province {
              name
            }
            links {
              type
              url
              label
            }
          }
          meta {
            total
            perPage
            lastPage
          }
        }
      }
    `,
    { filters: variables }
  );

/** Un empleado de la tienda, con lo justo para ofrecerlo como atajo. */
export type StoreEmployeeOption = {
  id: number;
  role: UserRole;
  user: {
    id: number;
    name: string;
    lastName: string;
    cossyId: Nullable<string>;
    picture: Nullable<string>;
  };
};

/**
 * Los empleados de una tienda, sin el resto de la tienda.
 *
 * `getStore` trae fotos, horarios, links y los torneos recurrentes: para
 * ofrecer atajos de staff no se mira nada de eso.
 */
export const getStoreEmployees = async (
  user: Nullable<UserWithToken>,
  id: number
) =>
  getGraphQLQueryClient(user).request<
    WithData<{ id: number; employees: Nullable<StoreEmployeeOption[]> }>
  >(
    gql`
      query storeEmployees($id: Int!) {
        data: store(id: $id) {
          id
          employees {
            id
            role
            user {
              id
              name
              lastName
              cossyId
              picture
            }
          }
        }
      }
    `,
    { id }
  );

export const getStore = async (user: Nullable<UserWithToken>, id: number) =>
  getGraphQLQueryClient(user).request<WithData<Store>>(
    gql`
      query store($id: Int!) {
        data: store(id: $id) {
          id
          name
          description
          slug
          address
          city
          isOTS
          logoUrl
          photos
          tags
          zipCode
          latitude
          longitude
          googleMapsUrl
          isCommunity
          hidden
          openingHours {
            day
            isClosed
            schedule {
              open
              close
            }
          }
          links {
            type
            url
            label
          }
          province {
            id
          }
          recurringTournaments {
            id
            title
            day
            startTime
            endTime
            frequency
            discordChannelId
            format {
              id
              name
            }
            price
          }
          employees {
            id
            role
            user {
              id
              name
              lastName
              email
              cossyId
            }
          }
          organizers {
            id
          }
        }
      }
    `,
    { id }
  );

export const createStore = async (
  user: Nullable<UserWithToken>,
  input: Partial<Store>
) =>
  getGraphQLQueryClient(user).request<WithData<Store>>(
    gql`
      mutation createStore($input: StoreInput!) {
        data: createStore(input: $input) {
          id
        }
      }
    `,
    { input }
  );

export const updateStore = async (
  user: Nullable<UserWithToken>,
  id: number,
  data: Partial<Store>
) =>
  getGraphQLQueryClient(user).request<WithData<Store>>(
    gql`
      mutation updateStore($id: Int!, $input: StoreInput!) {
        data: updateStore(id: $id, input: $input) {
          id
        }
      }
    `,
    { id, input: data }
  );

export const createStoreTournament = async (
  user: Nullable<UserWithToken>,
  input: Omit<StoreTournament, 'id' | 'format'>
) =>
  getGraphQLQueryClient(user).request<WithData<StoreTournament>>(
    gql`
      mutation createStoreTournament($input: StoreTournamentInput!) {
        data: createStoreTournament(input: $input) {
          id
        }
      }
    `,
    { input }
  );

export const updateStoreTournament = async (
  user: Nullable<UserWithToken>,
  id: number,
  input: Omit<StoreTournament, 'id' | 'format'>
) =>
  getGraphQLQueryClient(user).request<WithData<StoreTournament>>(
    gql`
      mutation updateStoreTournament($id: Int!, $input: StoreTournamentInput!) {
        data: updateStoreTournament(id: $id, input: $input) {
          id
        }
      }
    `,
    { id, input }
  );

export const deleteStoreTournament = async (
  user: Nullable<UserWithToken>,
  id: number
) =>
  getGraphQLQueryClient(user).request(
    gql`
      mutation deleteStoreTournament($id: Int!) {
        data: deleteStoreTournament(id: $id) {
          id
        }
      }
    `,
    { id }
  );

export const addStoreEmployee = async (
  user: Nullable<UserWithToken>,
  storeId: number,
  userId: number,
  role: string
) =>
  getGraphQLQueryClient(user).request(
    gql`
      mutation addStoreEmployee(
        $storeId: Int!
        $userId: Int!
        $role: UserRole!
      ) {
        data: addStoreEmployee(
          storeId: $storeId
          userId: $userId
          role: $role
        ) {
          id
        }
      }
    `,
    { storeId, userId, role }
  );

export const updateStoreEmployee = async (
  user: Nullable<UserWithToken>,
  id: number,
  role: string
) =>
  getGraphQLQueryClient(user).request(
    gql`
      mutation updateStoreEmployee($id: Int!, $role: UserRole!) {
        data: updateStoreEmployee(id: $id, role: $role) {
          id
        }
      }
    `,
    { id, role }
  );

export const removeStoreEmployee = async (
  user: Nullable<UserWithToken>,
  id: number
) =>
  getGraphQLQueryClient(user).request(
    gql`
      mutation removeStoreEmployee($id: Int!) {
        data: removeStoreEmployee(id: $id) {
          id
        }
      }
    `,
    { id }
  );

export const uploadStoreLogo = (
  storeId: number,
  file: File,
  config?: AxiosRequestConfig
) => {
  const formData = new FormData();
  formData.append('logo', file);

  return axiosClient.post(`/stores/${storeId}/logo`, formData, {
    ...config,
    headers: {
      ...config?.headers,
      'Content-Type': 'multipart/form-data',
    },
  });
};

export const deleteStoreLogo = (
  storeId: number,
  config?: AxiosRequestConfig
) => {
  return axiosClient.delete(`/stores/${storeId}/logo`, config);
};

export const uploadStorePhoto = (
  storeId: number,
  file: File,
  config?: AxiosRequestConfig
) => {
  const formData = new FormData();
  formData.append('photo', file);

  return axiosClient.post(`/stores/${storeId}/photos`, formData, {
    ...config,
    headers: {
      ...config?.headers,
      'Content-Type': 'multipart/form-data',
    },
  });
};

export const deleteStorePhoto = (
  storeId: number,
  photoKey: string,
  config?: AxiosRequestConfig
) => {
  return axiosClient.delete(
    `/stores/${storeId}/photos/${encodeURIComponent(photoKey)}`,
    config
  );
};

// Admin-only: el canal de Discord de una plantilla recurrente. Se propaga a sus
// torneos que todavía no arrancaron; los que se crean después lo heredan.
export const setStoreTournamentDiscordChannel = async (
  user: Nullable<UserWithToken>,
  id: number,
  discordChannelId: Nullable<string>
) =>
  getGraphQLQueryClient(user).request<WithData<StoreTournament>>(
    gql`
      mutation setStoreTournamentDiscordChannel(
        $id: Int!
        $discordChannelId: String
      ) {
        data: setStoreTournamentDiscordChannel(
          id: $id
          discordChannelId: $discordChannelId
        ) {
          id
          discordChannelId
        }
      }
    `,
    { id, discordChannelId }
  );
